/* Lunafreya Glass Header — Header behaviour on scroll: overlay, reveal, scrolled design. */
(() => {
  const KEY = '__LunafreyaHeaderBehavior';
  const VERSION = '34.0.0';
  const ROOT_SELECTOR = '[data-luna-header-v25]';
  const VALID = new Set(['always','static','reveal']);

  /* Remove the previous V33 controller once, then keep one stable manager. */
  try { window.__LunafreyaHeaderBehaviorV33?.destroyAll?.(); } catch (_) {}
  if (window[KEY]?.version === VERSION) {
    window[KEY].init(document);
    return;
  }
  try { window[KEY]?.destroyAll?.(); } catch (_) {}

  class LunaHeaderBehavior {
    constructor(root) {
      if (!root || root._lunaHeaderBehavior) return;
      root._lunaHeaderBehavior = this;

      this.root = root;
      this.surface = root.querySelector('[data-luna-header-surface]');
      this.scrollLine = root.querySelector('.luna-scroll-line');
      if (!this.surface) return;

      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.sourceAbort = null;
      this.resizeObserver = null;
      this.source = window;
      this.mobileQuery = window.matchMedia('(max-width:989px)');

      this.mode = '';
      this.lastY = 0;
      this.downTravel = 0;
      this.upTravel = 0;
      this.hidden = false;
      this.appearancePassed = null;
      this.scrollRange = 1;
      this.raf = 0;
      this.refreshRaf = 0;
      this.scrollIdleTimer = 0;
      this.lastScrollEventAt = 0;
      this.scrollActive = false;
      this.spacer = null;
      this.started = false;

      /* These do not run scroll calculations; they only restore visibility. */
      this.root.addEventListener('pointerdown', () => this.show(), {signal:this.signal, passive:true});
      this.root.addEventListener('focusin', () => this.show(), {signal:this.signal});

      const refresh = () => this.queueRefresh();
      window.addEventListener('resize', refresh, {signal:this.signal, passive:true});
      window.addEventListener('orientationchange', refresh, {signal:this.signal, passive:true});
      window.addEventListener('pageshow', refresh, {signal:this.signal, passive:true});
      document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); }, {signal:this.signal});
      this.mobileQuery.addEventListener?.('change', refresh, {signal:this.signal});

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.start(), {once:true, signal:this.signal});
      } else {
        this.start();
      }
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.bindSource();
      this.applyMode(true);
      this.refreshMetrics();
      this.lastY = this.readY();
      this.applyAppearance(this.lastY, true);
      this.updateScrollIndicator(this.lastY, true);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueRefresh(false));
        this.resizeObserver.observe(this.surface);
      }

      /* One post-layout refresh. No continuous geometry measuring while scrolling. */
      requestAnimationFrame(() => this.queueRefresh(false));
    }

    getMode() {
      const raw = this.mobileQuery.matches ? this.root.dataset.behaviorMobile : this.root.dataset.behaviorDesktop;
      return VALID.has(raw) ? raw : 'reveal';
    }

    findPageScroller() {
      const main = document.querySelector('#MainContent, main');
      let node = main;
      while (node && node !== document.body && node !== document.documentElement) {
        const style = getComputedStyle(node);
        const overflow = `${style.overflowY} ${style.overflow}`;
        if (/(auto|scroll|overlay)/.test(overflow) && node.scrollHeight > node.clientHeight + 4) return node;
        node = node.parentElement;
      }
      return window;
    }

    bindSource() {
      this.sourceAbort?.abort();
      this.sourceAbort = new AbortController();
      const signal = this.sourceAbort.signal;
      this.source = this.findPageScroller();

      /* Exactly one active scroll source. The handler performs no layout reads. */
      const handler = () => this.queueUpdate();
      if (this.source === window) {
        window.addEventListener('scroll', handler, {signal, passive:true});
      } else {
        this.source.addEventListener('scroll', handler, {signal, passive:true});
      }
    }

    readY() {
      if (this.source && this.source !== window && document.contains(this.source)) {
        return Math.max(0, Number(this.source.scrollTop) || 0);
      }
      const scroller = document.scrollingElement || document.documentElement || document.body;
      return Math.max(0, Number(window.scrollY || window.pageYOffset || scroller?.scrollTop || 0) || 0);
    }

    queueUpdate() {
      this.markScrollActive();
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.update();
      });
    }

    queueRefresh(rebind=true) {
      if (this.refreshRaf) return;
      this.refreshRaf = requestAnimationFrame(() => {
        this.refreshRaf = 0;
        if (!this.started) return;
        if (rebind) this.bindSource();
        this.applyMode(true);
        this.refreshMetrics();
        const y = this.readY();
        this.lastY = y;
        this.applyAppearance(y, true);
        this.updateScrollIndicator(y, true);
      });
    }

    markScrollActive() {
      this.lastScrollEventAt = performance.now();
      if (!this.scrollActive) {
        this.scrollActive = true;
        this.root.classList.add('luna-scroll-active');
      }
      if (!this.scrollIdleTimer) {
        this.scrollIdleTimer = window.setTimeout(() => this.checkScrollIdle(), 120);
      }
    }

    checkScrollIdle() {
      const elapsed = performance.now() - this.lastScrollEventAt;
      if (elapsed < 110) {
        this.scrollIdleTimer = window.setTimeout(() => this.checkScrollIdle(), Math.max(24, 120 - elapsed));
        return;
      }
      this.scrollIdleTimer = 0;
      this.scrollActive = false;
      this.root.classList.remove('luna-scroll-active');
    }

    applyMode(force=false) {
      const next = this.getMode();
      if (!force && next === this.mode) return;
      this.mode = next;
      this.root.classList.remove('luna-behavior--always','luna-behavior--static','luna-behavior--reveal','luna-behavior-hidden');
      this.root.classList.add(`luna-behavior--${next}`);
      this.hidden = false;
      this.downTravel = 0;
      this.upTravel = 0;
      this.root.dataset.behaviorModeActive = next;
      this.root.dataset.behaviorState = 'visible';
      this.updateFlowSpace();
    }

    applyAppearance(y, force=false) {
      const threshold = Math.max(0, Number(this.root.dataset.scrollThreshold) || 80);
      const passed = y >= threshold;
      if (!force && passed === this.appearancePassed) return;
      this.appearancePassed = passed;
      this.root.classList.toggle('is-at-top', !passed);
      this.root.classList.toggle('is-scrolled', passed);
    }

    update() {
      if (!this.started) return;
      const y = this.readY();
      this.applyAppearance(y);
      this.updateScrollIndicator(y);

      const locked = this.root.classList.contains('has-open-panel') || this.root.classList.contains('has-open-dropdown');
      if (this.mode !== 'reveal' || locked) {
        this.setHidden(false);
        this.downTravel = 0;
        this.upTravel = 0;
        this.lastY = y;
        return;
      }

      if (y <= 4) {
        this.setHidden(false);
        this.downTravel = 0;
        this.upTravel = 0;
        this.lastY = y;
        return;
      }

      const delta = y - this.lastY;
      if (delta > 0.75) {
        this.downTravel += delta;
        this.upTravel = 0;
        if (this.downTravel >= 12) {
          this.setHidden(true);
          this.downTravel = 0;
        }
      } else if (delta < -0.75) {
        this.upTravel += -delta;
        this.downTravel = 0;
        if (this.upTravel >= 8) {
          this.setHidden(false);
          this.upTravel = 0;
        }
      }

      this.lastY = y;
    }

    setHidden(hidden) {
      if (this.mode !== 'reveal') hidden = false;
      if (this.hidden === hidden) return;
      this.hidden = hidden;
      this.root.classList.toggle('luna-behavior-hidden', hidden);
      this.root.dataset.behaviorState = hidden ? 'hidden' : 'visible';
    }

    show() {
      if (!this.started) return;
      this.setHidden(false);
      this.downTravel = 0;
      this.upTravel = 0;
      this.lastY = this.readY();
    }

    refreshMetrics() {
      this.updateFlowSpace();
      this.updatePanelGeometry();
      const scroller = this.source === window
        ? (document.scrollingElement || document.documentElement || document.body)
        : this.source;
      const viewport = this.source === window ? window.innerHeight : (scroller?.clientHeight || 0);
      this.scrollRange = Math.max(1, (scroller?.scrollHeight || document.documentElement.scrollHeight || 1) - viewport);
    }

    updateFlowSpace() {
      const needsSpace = (this.root.dataset.overlayMode || 'overlay') === 'flow' && this.mode !== 'static';
      if (!needsSpace) {
        this.spacer?.remove();
        this.spacer = null;
        return;
      }
      if (!this.spacer) {
        this.spacer = document.createElement('div');
        this.spacer.className = 'luna-header-flow-space';
        this.spacer.setAttribute('aria-hidden','true');
        this.root.after(this.spacer);
      }
      const h = Math.max(1, Math.ceil(this.surface.offsetHeight || 1));
      if (this.spacer._lunaHeight !== h) {
        this.spacer._lunaHeight = h;
        this.spacer.style.height = `${h}px`;
      }
    }

    updatePanelGeometry() {
      const rect = this.surface.getBoundingClientRect();
      this.root.style.setProperty('--luna-drop-top', `${Math.max(0, rect.bottom + 6)}px`);
      this.root.style.setProperty('--luna-panel-top', `${Math.max(0, rect.bottom)}px`);
    }

    updateScrollIndicator(y, force=false) {
      if (!this.scrollLine) return;
      const progress = Math.max(0, Math.min(1, y / this.scrollRange));
      if (!force && Math.abs(progress - (this.lastIndicatorProgress ?? -1)) < 0.0015) return;
      this.lastIndicatorProgress = progress;
      this.scrollLine.style.transform = `scaleX(${progress.toFixed(4)})`;
    }

    destroy() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.refreshRaf) cancelAnimationFrame(this.refreshRaf);
      if (this.scrollIdleTimer) clearTimeout(this.scrollIdleTimer);
      this.sourceAbort?.abort();
      this.resizeObserver?.disconnect();
      this.abort.abort();
      this.spacer?.remove();
      this.root.classList.remove('luna-behavior--always','luna-behavior--static','luna-behavior--reveal','luna-behavior-hidden','luna-scroll-active','is-scrolled','is-at-top');
      if (this.scrollLine) this.scrollLine.style.removeProperty('transform');
      delete this.root.dataset.behaviorModeActive;
      delete this.root.dataset.behaviorState;
      delete this.root._lunaHeaderBehavior;
    }
  }

  const instances = new Set();
  const api = {
    version: VERSION,
    init(scope=document) {
      [...instances].forEach(instance => {
        if (document.contains(instance.root)) return;
        instance.destroy();
        instances.delete(instance);
      });
      const roots = scope.matches?.(ROOT_SELECTOR) ? [scope] : [...scope.querySelectorAll(ROOT_SELECTOR)];
      roots.forEach(root => {
        if (root._lunaHeaderBehavior) return;
        const instance = new LunaHeaderBehavior(root);
        if (instance?.surface) instances.add(instance);
      });
    },
    destroy(scope) {
      const roots = scope?.matches?.(ROOT_SELECTOR) ? [scope] : [...(scope?.querySelectorAll?.(ROOT_SELECTOR) || [])];
      roots.forEach(root => {
        const instance = root._lunaHeaderBehavior;
        if (!instance) return;
        instance.destroy();
        instances.delete(instance);
      });
    },
    refresh(root) {
      root?._lunaHeaderBehavior?.queueRefresh(true);
    },
    destroyAll() {
      [...instances].forEach(instance => instance.destroy());
      instances.clear();
    }
  };

  window[KEY] = api;
  document.addEventListener('shopify:section:load', event => requestAnimationFrame(() => api.init(event.target)));
  document.addEventListener('shopify:section:unload', event => api.destroy(event.target));
  window.addEventListener('load', () => document.querySelectorAll(ROOT_SELECTOR).forEach(root => api.refresh(root)), {once:true, passive:true});
  api.init(document);
})();
